import React, {
  useState,
  useEffect,
  useRef,
  useContext,
} from "react";

import axios from "axios";
import { useNavigate } from "react-router-dom";
import { AppContext } from "../../../context/AppContext";
import "../../../assets/css/patient/patientauth/Login.css";
import Swal from "sweetalert2";

// ======================================================
// OAUTH URLs
// ======================================================

const OAUTH_URLS = {
  google:
    "https://accounts.google.com/o/oauth2/v2/auth?client_id=YOUR_GOOGLE_CLIENT_ID&redirect_uri=YOUR_REDIRECT_URI&response_type=code&scope=openid%20email%20profile",

  facebook:
    "https://www.facebook.com/v18.0/dialog/oauth?client_id=YOUR_FB_APP_ID&redirect_uri=YOUR_REDIRECT_URI&scope=email,public_profile",

  apple:
    "https://appleid.apple.com/auth/authorize?client_id=YOUR_APPLE_CLIENT_ID&redirect_uri=YOUR_REDIRECT_URI&response_type=code&scope=name%20email",
};

// ======================================================
// COMPONENT
// ======================================================

const PatientLogin = ({ isOpen, onClose }) => {
  const { baseUrl, login } = useContext(AppContext);

  const navigate = useNavigate();

  // ====================================================
  // STATES
  // ====================================================

  const [isLogin, setIsLogin] = useState(false);

  const [email, setEmail] = useState("");

  const [password, setPassword] = useState("");

  const [fullName, setFullName] = useState("");

  const [confirmPassword, setConfirmPassword] =
    useState("");

  const [loading, setLoading] = useState(false);

  // ====================================================
  // TOAST STATE
  // ====================================================

  const [toast, setToast] = useState({
    show: false,
    type: "",
    title: "",
    message: "",
  });

  const timerRef = useRef(null);

  // ====================================================
  // SHOW TOAST
  // ====================================================

  const showToast = (type, title, message) => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    setToast({
      show: true,
      type,
      title,
      message,
    });

    timerRef.current = setTimeout(() => {
      setToast((previous) => ({
        ...previous,
        show: false,
      }));
    }, 3000);
  };

  // ====================================================
  // HIDE TOAST
  // ====================================================

  const hideToast = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    setToast((previous) => ({
      ...previous,
      show: false,
    }));
  };

  // ====================================================
  // CLEANUP TIMER
  // ====================================================

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  // ====================================================
  // OAUTH LOGIN
  // ====================================================

  const handleOAuth = (provider) => {
    const url = OAUTH_URLS[provider];

    if (!url) {
      showToast(
        "error",
        "OAuth Error",
        "This login provider is not configured."
      );

      return;
    }

    const width = 500;
    const height = 600;

    const left =
      window.screenX +
      (window.outerWidth - width) / 2;

    const top =
      window.screenY +
      (window.outerHeight - height) / 2;

    window.open(
      url,
      `${provider}_oauth`,
      `width=${width},height=${height},left=${left},top=${top}`
    );
  };

  // ====================================================
  // CLEAR FORM
  // ====================================================

  const clearForm = () => {
    setEmail("");
    setPassword("");
    setFullName("");
    setConfirmPassword("");
  };

  // ====================================================
  // PATIENT LOGIN
  // ====================================================

  const patientLogin = async () => {
    // -----------------------------------------------
    // Validation
    // -----------------------------------------------

    if (!email.trim() || !password.trim()) {
      showToast(
        "error",
        "Fields Incomplete",
        "Please enter your email and password."
      );

      return;
    }

    setLoading(true);

    try {
      // ---------------------------------------------
      // Login API (baseUrl already contains /api)
      // ---------------------------------------------

      const response = await axios.post(
        `${baseUrl}/Patient/PatientAuth/login`,
        {
          email: email.trim(),
          password: password,
        },
        {
          withCredentials: true,
        }
      );

      console.log(
        "Login Response:",
        response.data
      );
      console.log("Login Response:", response.data);

      // ---------------------------------------------
      // Save patient + token in AppContext
      // ---------------------------------------------

      login(
        response.data.token,
        response.data.patient
      );

      // ---------------------------------------------
      // Sanctum token localStorage mein save
      // (AI chat aur baaki protected routes ke liye zaroori hai —
      // key naam "patient_token" hi rakhein, kyunke baaki saari
      // pages/components isi key se Authorization header banate hain)
      // ---------------------------------------------

      localStorage.setItem(
        "patient_token",
        response.data.token
      );

      localStorage.setItem(
        "userId",
        response.data.patient.id
      );

      // ---------------------------------------------
      // Success Message
      // ---------------------------------------------

      await Swal.fire({
        icon: "success",
        title: "Welcome Back!",
        text: "You have successfully logged in.",
        confirmButtonText: "Go to Dashboard",
        confirmButtonColor: "#4CAF50",
        timer: 3000,
        timerProgressBar: true,
      });

      onClose();

      navigate("/patientdashboard/mysettings");

    } catch (error) {
      console.error(
        "Login Error:",
        error
      );

      console.log(
        "Login Backend Error:",
        error?.response?.data
      );

      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        "Invalid email or password. Please try again.";

      Swal.fire({
        icon: "error",
        title: "Login Failed",
        text: message,
        confirmButtonText: "Try Again",
        confirmButtonColor: "#e53935",
      });

    } finally {
      setLoading(false);
    }
  };

  // ====================================================
  // PATIENT REGISTER
  // ====================================================

  const patientRegister = async () => {
    // -----------------------------------------------
    // Required fields
    // -----------------------------------------------

    if (
      !fullName.trim() ||
      !email.trim() ||
      !password ||
      !confirmPassword
    ) {
      showToast(
        "error",
        "Fields Incomplete",
        "Please complete all required fields."
      );

      return;
    }

    // -----------------------------------------------
    // Email validation
    // -----------------------------------------------

    const emailRegex =
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailRegex.test(email.trim())) {
      showToast(
        "error",
        "Invalid Email",
        "Please enter a valid email address."
      );

      return;
    }

    // -----------------------------------------------
    // Password length
    // -----------------------------------------------

    if (password.length < 8) {
      showToast(
        "error",
        "Weak Password",
        "Password must contain at least 8 characters."
      );

      return;
    }

    // -----------------------------------------------
    // Password confirmation
    // -----------------------------------------------

    if (password !== confirmPassword) {
      showToast(
        "error",
        "Password Mismatch",
        "Passwords do not match. Please try again."
      );

      return;
    }

    setLoading(true);

    try {
      // ---------------------------------------------
      // Register API
      // ---------------------------------------------

      const response = await axios.post(
        `${baseUrl}/Patient/PatientAuth/register`,
        {
          name: fullName.trim(),
          email: email.trim(),
          password: password,
          confirmpassword: confirmPassword,
        }
      );

      console.log(
        "Registration Response:",
        response.data
      );

      // ---------------------------------------------
      // Success
      // ---------------------------------------------

      await Swal.fire({
        icon: "success",
        title: "Account Created!",
        text: `Welcome ${fullName}! Your account is ready.`,
        confirmButtonText: "Login Now",
        confirmButtonColor: "#4CAF50",
      });

      // ---------------------------------------------
      // Switch to Login
      // ---------------------------------------------

      setIsLogin(true);

      // ---------------------------------------------
      // Clear Form
      // ---------------------------------------------

      setEmail("");
      setPassword("");
      setFullName("");
      setConfirmPassword("");

    } catch (error) {
      console.error(
        "Registration Error:",
        error
      );

      // IMPORTANT:
      // Browser console mein actual Laravel error show hoga
      console.log(
        "Registration Backend Error:",
        error?.response?.data
      );

      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        "Something went wrong. Please try again.";

      Swal.fire({
        icon: "error",
        title: "Registration Failed",
        text: message,
        confirmButtonText: "Try Again",
        confirmButtonColor: "#e53935",
      });

    } finally {
      setLoading(false);
    }
  };

  // ====================================================
  // SWITCH LOGIN / REGISTER
  // ====================================================

  const toggleLoginMode = () => {
    setIsLogin((previous) => !previous);

    clearForm();

    hideToast();
  };

  // ====================================================
  // MODAL CHECK
  // ====================================================

  if (!isOpen) {
    return null;
  }

  // ====================================================
  // UI
  // ====================================================

  return (
    <>
      {/* ==================================================
          FONT AWESOME
      ================================================== */}

      <link
        rel="stylesheet"
        href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.1/css/all.min.css"
      />

      {/* ==================================================
          TOAST
      ================================================== */}

      <div
        className={`patient-toast ${
          toast.type
        } ${toast.show ? "show" : ""}`}
      >
        <span className="patient-toast-icon">
          {toast.type === "success"
            ? "✅"
            : "⚠️"}
        </span>

        <div className="patient-toast-body">
          <div className="patient-toast-title">
            {toast.title}
          </div>

          <div className="patient-toast-msg">
            {toast.message}
          </div>
        </div>

        <button
          type="button"
          className="patient-toast-close"
          onClick={hideToast}
        >
          ×
        </button>

        {toast.show && (
          <div
            className={`patient-toast-bar ${toast.type}`}
          />
        )}
      </div>

      {/* ==================================================
          MODAL OVERLAY
      ================================================== */}

      <div
        className="patient-modal-overlay"
        onClick={onClose}
      >
        <div
          className="patient-modal-content"
          onClick={(e) => e.stopPropagation()}
        >

          {/* ==================================================
              CLOSE BUTTON
          ================================================== */}

          <button
            type="button"
            onClick={onClose}
            className="patient-close-button"
          >
            ×
          </button>

          {/* ==================================================
              MAIN CONTAINER
          ================================================== */}

          <div
            className={`patient-modal-container ${
              isLogin
                ? "login-mode"
                : "signup-mode"
            }`}
          >

            {/* ==================================================
                FORM SECTION
            ================================================== */}

            <div className="patient-form-section">

              {/* Header */}

              <div className="patient-form-header">
                <div className="patient-form-logo">
                  🏥 Patient Portal
                </div>
              </div>

              {/* Title */}

              <h2 className="patient-form-title">
                {isLogin
                  ? "Patient Login"
                  : "Patient Registration"}
              </h2>

              {/* Subtitle */}

              <p className="patient-form-subtitle">
                {isLogin
                  ? "Access your healthcare dashboard"
                  : "Create your patient account"}
              </p>

              {/* ==================================================
                  SOCIAL BUTTONS
              ================================================== */}

              <div className="patient-social-buttons-row">

                {/* Google */}

                <button
                  type="button"
                  className="patient-social-button"
                  onClick={() =>
                    handleOAuth("google")
                  }
                >
                  <i className="fa-brands fa-google"></i>
                  {" "}Google
                </button>

              </div>

              {/* ==================================================
                  DIVIDER
              ================================================== */}

              <div className="patient-divider-container">
                <span className="patient-divider-text">
                  or use your email
                </span>
              </div>

              {/* ==================================================
                  FORM FIELDS
              ================================================== */}

              <div className="patient-form-fields">

                {/* ==================================================
                    FULL NAME
                ================================================== */}

                {!isLogin && (
                  <div className="patient-input-group">

                    <input
                      type="text"
                      placeholder="Full Name"
                      value={fullName}
                      onChange={(e) =>
                        setFullName(
                          e.target.value
                        )
                      }
                      className="patient-form-input"
                      disabled={loading}
                    />

                    <span className="patient-input-icon">
                      👤
                    </span>

                  </div>
                )}

                {/* ==================================================
                    EMAIL
                ================================================== */}

                <div className="patient-input-group">

                  <input
                    type="email"
                    placeholder="Email Address"
                    value={email}
                    onChange={(e) =>
                      setEmail(
                        e.target.value
                      )
                    }
                    className="patient-form-input"
                    disabled={loading}
                  />

                  <span className="patient-input-icon">
                    📧
                  </span>

                </div>

                {/* ==================================================
                    PASSWORD
                ================================================== */}

                <div
                  className={`patient-input-group ${
                    isLogin
                      ? "password-login"
                      : ""
                  }`}
                >

                  <input
                    type="password"
                    placeholder="Password"
                    value={password}
                    onChange={(e) =>
                      setPassword(
                        e.target.value
                      )
                    }
                    className="patient-form-input"
                    disabled={loading}
                  />

                  <span className="patient-input-icon">
                    🔒
                  </span>

                </div>

                {/* ==================================================
                    CONFIRM PASSWORD
                ================================================== */}

                {!isLogin && (
                  <div className="patient-input-group">

                    <input
                      type="password"
                      placeholder="Confirm Password"
                      value={confirmPassword}
                      onChange={(e) =>
                        setConfirmPassword(
                          e.target.value
                        )
                      }
                      className="patient-form-input"
                      disabled={loading}
                    />

                    <span className="patient-input-icon">
                      🔐
                    </span>

                  </div>
                )}

                {/* ==================================================
                    FORGOT PASSWORD
                ================================================== */}

                {isLogin && (
                  <div className="patient-forgot-password">

                    <a
                      href="#"
                      className="patient-forgot-link"
                      onClick={(e) => {
                        e.preventDefault();

                        showToast(
                          "success",
                          "Forgot Password",
                          "Password recovery will be available soon."
                        );
                      }}
                    >
                      Forgot Password?
                    </a>

                  </div>
                )}

                {/* ==================================================
                    SUBMIT BUTTON
                ================================================== */}

                <button
                  type="button"
                  onClick={
                    isLogin
                      ? patientLogin
                      : patientRegister
                  }
                  className="patient-submit-button"
                  disabled={loading}
                >
                  {loading
                    ? "Please wait..."
                    : isLogin
                    ? "Login to Dashboard"
                    : "Create Patient Account"}
                </button>

                {/* ==================================================
                    TERMS
                ================================================== */}

                {!isLogin && (
                  <p className="patient-terms-text">
                    By registering, you agree to our
                    Terms of Service and Privacy
                    Policy.
                  </p>
                )}

              </div>
            </div>

            {/* ==================================================
                WELCOME SECTION
            ================================================== */}

            <div className="patient-welcome-section">

              {/* Decorative circles */}

              <div className="patient-welcome-circle patient-welcome-circle-1"></div>

              <div className="patient-welcome-circle patient-welcome-circle-2"></div>

              {/* Welcome title */}

              <h2 className="patient-welcome-title">
                {isLogin
                  ? "Welcome Back!"
                  : "Join Our Community!"}
              </h2>

              {/* Icon */}

              <div className="patient-welcome-icon">
                <i className="fa-solid fa-user-injured"></i>
              </div>

              {/* Body */}

              <div className="patient-welcome-body">

                <p className="patient-welcome-text">
                  {isLogin
                    ? "Access your appointments, medical records, and connect with healthcare professionals."
                    : "Register to book appointments, track your health journey, and get personalized care."}
                </p>

                {/* Toggle */}

                <button
                  type="button"
                  onClick={toggleLoginMode}
                  className="patient-toggle-button"
                >
                  {isLogin
                    ? "Register Now"
                    : "Already Registered?"}
                </button>

              </div>
            </div>

          </div>
        </div>
      </div>
    </>
  );
};

export default PatientLogin;